import React, { useState, useEffect, useRef } from 'react';
import {
  CONSTRUCTION_CATEGORIES,
  fetchConstructionServices,
  addConstructionService,
  updateConstructionService,
  deleteConstructionService,
  toggleServiceStatus,
  toggleFooterVisibility,
  uploadServiceImage
} from '../../services/constructionService';
import { useAuth } from '../../controllers/auth';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  Hammer,
  Building2,
  Layers,
  Store,
  Warehouse,
  Wrench,
  Sparkles,
  ShieldCheck,
  Compass,
  FileText,
  Paintbrush,
  Boxes,
  Pipette,
  Zap,
  TreePine,
  Grid,
  ClipboardCheck,
  CheckCircle2,
  HardHat,
  Home,
  Plus,
  Trash2,
  Edit2,
  Search,
  Upload,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  X,
  Filter
} from 'lucide-react';

const ICON_MAP = {
  Hammer,
  Building2,
  Layers,
  Store,
  Warehouse,
  Wrench,
  Sparkles,
  ShieldCheck,
  Compass,
  FileText,
  Paintbrush,
  Boxes,
  Pipette,
  Zap,
  TreePine,
  Grid,
  ClipboardCheck,
  CheckCircle2,
  HardHat,
  Home
};

const AVAILABLE_ICONS = Object.keys(ICON_MAP);

const ConstructionServices = () => {
  const { logAction } = useAuth();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    category: 'Civil Construction',
    name: '',
    description: '',
    icon: 'Hammer',
    image_url: '',
    priority: 1,
    status: 'Active',
    show_in_footer: true
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const fileInputRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    const data = await fetchConstructionServices();
    setServices(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenModal = (service = null) => {
    setFormError('');
    setImageFile(null);

    if (service) {
      setEditingService(service);
      setFormData({
        category: service.category || 'Civil Construction',
        name: service.name || '',
        description: service.description || '',
        icon: service.icon || 'Hammer',
        image_url: service.image_url || '',
        priority: service.priority || 1,
        status: service.status || 'Active',
        show_in_footer: service.show_in_footer !== false
      });
      setImagePreview(service.image_url || '');
    } else {
      setEditingService(null);
      setFormData({
        category: categoryFilter !== 'ALL' ? categoryFilter : 'Civil Construction',
        name: '',
        description: '',
        icon: 'Hammer',
        image_url: '',
        priority: services.length + 1,
        status: 'Active',
        show_in_footer: true
      });
      setImagePreview('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
    setImageFile(null);
    setImagePreview('');
    setFormError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFormError('Please select a valid image file (JPG, PNG, WebP).');
      return;
    }

    setImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Service Name is required.');
      return;
    }

    if (!formData.description.trim()) {
      setFormError('Short description is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      let finalImageUrl = formData.image_url;

      if (imageFile) {
        finalImageUrl = await uploadServiceImage(imageFile);
      }

      const payload = {
        category: formData.category,
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon,
        image_url: finalImageUrl,
        priority: Number(formData.priority) || 0,
        status: formData.status,
        show_in_footer: formData.show_in_footer !== false
      };

      if (editingService) {
        await updateConstructionService(editingService.id, payload);
        if (logAction) {
          logAction('UPDATE_CONSTRUCTION_SERVICE', `Updated service "${payload.name}" in category ${payload.category}`);
        }
      } else {
        await addConstructionService(payload);
        if (logAction) {
          logAction('CREATE_CONSTRUCTION_SERVICE', `Created service "${payload.name}" in category ${payload.category}`);
        }
      }

      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving construction service:', err);
      setFormError(err.message || 'Failed to save service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (service) => {
    try {
      await toggleServiceStatus(service.id, service.status);
      if (logAction) {
        logAction('TOGGLE_CONSTRUCTION_STATUS', `Changed status of "${service.name}" to ${service.status === 'Active' ? 'Inactive' : 'Active'}`);
      }
      await loadData();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  };

  const handleToggleFooter = async (service) => {
    try {
      await toggleFooterVisibility(service.id, service.show_in_footer);
      if (logAction) {
        logAction('TOGGLE_CONSTRUCTION_FOOTER', `Toggled footer display for "${service.name}"`);
      }
      await loadData();
    } catch (err) {
      console.error('Error toggling footer visibility:', err);
    }
  };

  const handleDelete = (service) => {
    setConfirmTitle('Delete Construction Service');
    setConfirmMessage(`Are you sure you want to permanently delete "${service.name}"? This action cannot be undone.`);
    setPendingAction(() => async () => {
      try {
        await deleteConstructionService(service.id);
        if (logAction) {
          logAction('DELETE_CONSTRUCTION_SERVICE', `Deleted service "${service.name}"`);
        }
        await loadData();
      } catch (err) {
        console.error('Error deleting service:', err);
      }
    });
    setConfirmOpen(true);
  };

  // Filtered Services
  const filteredServices = services.filter((item) => {
    const matchesSearch =
      searchTerm === '' ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = categoryFilter === 'ALL' || item.category === categoryFilter;
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const renderIcon = (iconName, size = 18) => {
    const IconComponent = ICON_MAP[iconName] || Hammer;
    return <IconComponent size={size} />;
  };

  // Quick stats
  const totalCount = services.length;
  const activeCount = services.filter((s) => s.status === 'Active').length;
  const inactiveCount = services.filter((s) => s.status === 'Inactive').length;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '24px'
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              margin: '0 0 6px 0',
              letterSpacing: '-0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: 'rgba(168, 8, 12, 0.1)',
                color: '#A8080C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Hammer size={20} />
            </div>
            Construction & Renovation Services
          </h1>
          <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-secondary)' }}>
            Manage the category-wise services displayed on the public website and app.
          </p>
        </div>

        <button
          onClick={() => handleOpenModal()}
          style={{
            backgroundColor: '#A8080C',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 18px',
            fontSize: '13.5px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 6px rgba(168, 8, 12, 0.25)',
            transition: 'background-color 0.2s, transform 0.1s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8B0000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#A8080C';
          }}
        >
          <Plus size={16} /> Add New Service
        </button>
      </div>

      {/* Metric Counters */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}
      >
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(168, 8, 12, 0.08)',
              color: '#A8080C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HardHat size={22} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Services
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>{totalCount}</div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CheckCircle size={22} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Active on Website
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>{activeCount}</div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <XCircle size={22} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Inactive
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>{inactiveCount}</div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: '#3B82F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Layers size={22} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Categories
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {CONSTRUCTION_CATEGORIES.length}
            </div>
          </div>
        </div>
      </div>

      {/* Category Pills Filter */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '12.5px', fontWeight: '600' }}>
          <Filter size={14} /> FILTER BY CATEGORY:
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          <button
            onClick={() => setCategoryFilter('ALL')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '12.5px',
              fontWeight: '600',
              border: categoryFilter === 'ALL' ? '1px solid #A8080C' : '1px solid var(--border-primary)',
              backgroundColor: categoryFilter === 'ALL' ? '#A8080C' : 'var(--bg-primary)',
              color: categoryFilter === 'ALL' ? '#FFFFFF' : 'var(--text-primary)',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            All Categories ({services.length})
          </button>
          {CONSTRUCTION_CATEGORIES.map((cat) => {
            const count = services.filter((s) => s.category === cat).length;
            const isSelected = categoryFilter === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '12.5px',
                  fontWeight: '600',
                  border: isSelected ? '1px solid #A8080C' : '1px solid var(--border-primary)',
                  backgroundColor: isSelected ? '#A8080C' : 'var(--bg-primary)',
                  color: isSelected ? '#FFFFFF' : 'var(--text-primary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Status Bar */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: '450px' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-tertiary)'
            }}
          />
          <input
            type="text"
            placeholder="Search services by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px 9px 36px',
              fontSize: '13px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '9px 12px',
              fontSize: '13px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              outline: 'none'
            }}
          >
            <option value="ALL">All Statuses</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Services Table / Cards */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px auto', color: '#A8080C' }} />
            <p style={{ margin: 0, fontSize: '14px' }}>Loading construction services...</p>
          </div>
        ) : filteredServices.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <AlertCircle size={36} style={{ margin: '0 auto 12px auto', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: 'var(--text-primary)' }}>No services found</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13.5px' }}>
              {searchTerm || categoryFilter !== 'ALL' || statusFilter !== 'ALL'
                ? 'Try adjusting your filters or search keywords.'
                : 'Click "Add New Service" to create your first construction service.'}
            </p>
            <button
              onClick={() => handleOpenModal()}
              style={{
                backgroundColor: '#A8080C',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              + Add Service
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
              <thead>
                <tr
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderBottom: '1px solid var(--border-primary)',
                    color: 'var(--text-secondary)',
                    fontWeight: '600',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}
                >
                  <th style={{ padding: '12px 16px', width: '60px' }}>Order</th>
                  <th style={{ padding: '12px 16px', width: '80px' }}>Icon / Pic</th>
                  <th style={{ padding: '12px 16px' }}>Service Name & Details</th>
                  <th style={{ padding: '12px 16px', width: '180px' }}>Category</th>
                  <th style={{ padding: '12px 16px', width: '110px' }}>Status</th>
                  <th style={{ padding: '12px 16px', width: '110px' }}>In Footer</th>
                  <th style={{ padding: '12px 16px', width: '120px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((service, idx) => (
                  <tr
                    key={service.id || idx}
                    style={{
                      borderBottom: '1px solid var(--border-primary)',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Order */}
                    <td style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      #{service.priority || idx + 1}
                    </td>

                    {/* Image / Icon */}
                    <td style={{ padding: '14px 16px' }}>
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          backgroundColor: 'var(--bg-primary)',
                          border: '1px solid var(--border-primary)',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#A8080C'
                        }}
                      >
                        {service.image_url ? (
                          <img
                            src={service.image_url}
                            alt={service.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          renderIcon(service.icon, 22)
                        )}
                      </div>
                    </td>

                    {/* Details */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                        {service.name}
                      </div>
                      <div
                        style={{
                          fontSize: '12.5px',
                          color: 'var(--text-secondary)',
                          lineHeight: '1.4',
                          maxWidth: '480px'
                        }}
                      >
                        {service.description}
                      </div>
                    </td>

                    {/* Category */}
                    <td style={{ padding: '14px 16px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: 'rgba(168, 8, 12, 0.08)',
                          color: '#A8080C',
                          border: '1px solid rgba(168, 8, 12, 0.2)'
                        }}
                      >
                        {service.category}
                      </span>
                    </td>

                    {/* Status Toggle */}
                    <td style={{ padding: '14px 16px' }}>
                      <button
                        onClick={() => handleToggleStatus(service)}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 8px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor:
                            service.status === 'Active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: service.status === 'Active' ? '#10B981' : '#EF4444'
                        }}
                        title="Click to toggle status"
                      >
                        {service.status === 'Active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {service.status}
                      </button>
                    </td>

                    {/* Footer Toggle */}
                    <td style={{ padding: '14px 16px' }}>
                      <button
                        onClick={() => handleToggleFooter(service)}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 8px',
                          borderRadius: '16px',
                          fontSize: '11.5px',
                          fontWeight: '600',
                          backgroundColor:
                            service.show_in_footer !== false ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                          color: service.show_in_footer !== false ? '#10B981' : '#6B7280'
                        }}
                        title="Click to toggle footer visibility"
                      >
                        {service.show_in_footer !== false ? '✓ In Footer' : 'Hidden'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '8px' }}>
                        <button
                          onClick={() => handleOpenModal(service)}
                          style={{
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            borderRadius: '6px',
                            padding: '6px 9px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Edit Service"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(service)}
                          style={{
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            backgroundColor: 'rgba(239, 68, 68, 0.06)',
                            color: '#EF4444',
                            borderRadius: '6px',
                            padding: '6px 9px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete Service"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            backdropFilter: 'blur(3px)'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
              padding: '24px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                borderBottom: '1px solid var(--border-primary)',
                paddingBottom: '14px'
              }}
            >
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Hammer size={18} color="#A8080C" />
                {editingService ? 'Edit Construction Service' : 'Add New Construction Service'}
              </h2>
              <button
                onClick={handleCloseModal}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {formError && (
              <div
                style={{
                  padding: '10px 14px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  color: '#EF4444',
                  fontSize: '13px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <AlertCircle size={16} />
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Category */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}
                >
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13.5px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                >
                  {CONSTRUCTION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Name */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}
                >
                  Service Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Turnkey Home Construction"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13.5px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Short Description */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}
                >
                  Short Description *
                </label>
                <textarea
                  rows={3}
                  placeholder="Explain what is included in this service..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Icon & Priority Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '16px'
                }}
              >
                {/* Icon Selection */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '6px'
                    }}
                  >
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13.5px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-primary)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  >
                    {AVAILABLE_ICONS.map((ic) => (
                      <option key={ic} value={ic}>
                        {ic}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '6px'
                    }}
                  >
                    Display Order / Priority
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13.5px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-primary)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}
                >
                  Featured Image (Optional, max 1200px compressed)
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {imagePreview && (
                    <div
                      style={{
                        width: '54px',
                        height: '54px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--border-primary)',
                        flexShrink: 0
                      }}
                    >
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '1px solid var(--border-primary)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      borderRadius: '8px',
                      padding: '9px 14px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Upload size={14} /> {imagePreview ? 'Change Image' : 'Upload Image'}
                  </button>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                        setFormData({ ...formData, image_url: '' });
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#EF4444',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Status */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}
                >
                  Status
                </label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13.5px',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="Active"
                      checked={formData.status === 'Active'}
                      onChange={() => setFormData({ ...formData, status: 'Active' })}
                    />
                    <span style={{ color: '#10B981', fontWeight: '500' }}>Active (Visible on website)</span>
                  </label>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13.5px',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value="Inactive"
                      checked={formData.status === 'Inactive'}
                      onChange={() => setFormData({ ...formData, status: 'Inactive' })}
                    />
                    <span style={{ color: '#EF4444', fontWeight: '500' }}>Inactive</span>
                  </label>
                </div>
              </div>

              {/* Show in Footer Checkbox */}
              <div
                style={{
                  marginBottom: '24px',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-primary)'
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={formData.show_in_footer !== false}
                    onChange={(e) => setFormData({ ...formData, show_in_footer: e.target.checked })}
                    style={{ width: '18px', height: '18px', accentColor: '#A8080C', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '13.5px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      Show in Footer ("Our Services" list)
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      When checked, this service appears in the global website footer with a direct link to its card.
                    </div>
                  </div>
                </label>
              </div>

              {/* Modal Actions */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  borderTop: '1px solid var(--border-primary)',
                  paddingTop: '16px'
                }}
              >
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '9px 18px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '13.5px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '9px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#A8080C',
                    color: '#FFFFFF',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                >
                  {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                  {editingService ? 'Save Changes' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          if (pendingAction) await pendingAction();
          setConfirmOpen(false);
        }}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Delete Service"
        confirmVariant="danger"
      />
    </div>
  );
};

export default ConstructionServices;
